import React, { useState, useEffect } from "react";
import { useLocation, useHistory, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CynapsLogo as Logo } from "../../../components/CynapsLogo/CynapsLogo";
import "./HeroSection.css"; // The CSS for the pill navbar is in here
import logoImage from "../../../assets/images/logo.png";

export interface NavigationProps {
  isHeroPage?: boolean;
}

export const Navigation: React.FC<NavigationProps> = ({ isHeroPage }) => {
  const location = useLocation();
  const history = useHistory();

  // Has hero section if explicitly flagged or on root landing page
  const hasHero = isHeroPage !== undefined ? isHeroPage : location.pathname === "/";

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isResourcesHovered, setIsResourcesHovered] = useState(false);
  // Default to scrolled/black navbar if not on HeroSection
  const [isScrolled, setIsScrolled] = useState(!hasHero);

  useEffect(() => {
    if (!hasHero) {
      setIsScrolled(true);
      return;
    }

    const handleScroll = () => {
      // Transition at 75% of viewport height
      if (window.scrollY > window.innerHeight * 0.75) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasHero]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add("menu-open");
    } else {
      document.body.classList.remove("menu-open");
    }

    const handleResize = () => {
      if (window.innerWidth > 720 && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("keydown", handleEscape);
      document.body.classList.remove("menu-open");
    };
  }, [isMenuOpen]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleLogoClick = () => {
    if (location.pathname !== "/") {
      history.push("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Black navbar is active either when there's no hero section (default everywhere)
  // or when scrolled past the hero section on the landing page
  const showBlackNavbar = !hasHero || isScrolled;

  return (
    <>
      {/* State 1: The Pill Navbar (Only for HeroSection when not scrolled) */}
      {hasHero && (
        <motion.div
          initial={false}
          animate={{ 
            opacity: isScrolled ? 0 : 1, 
            scale: isScrolled ? 0.9 : 1,
            y: isScrolled ? -20 : 0 
          }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed top-0 left-0 right-0 z-50 px-[clamp(14px,3vw,32px)] pt-[clamp(16px,2.4vh,28px)] flex justify-center w-full"
          style={{ pointerEvents: isScrolled ? "none" : "auto" }}
        >
          <header className="hero-header" style={{ margin: 0, overflow: 'visible' }}>
            <button type="button" className="logo-btn" aria-label="Home" onClick={handleLogoClick}>
              <img src={logoImage} alt="Cynaps Logo" width="52" height="52" className="object-cover" />
            </button>

          <nav className="nav-pill" style={{ overflow: 'visible' }}>
            <a href="/docs" className="nav-link">
              Docs
            </a>
            
            <div 
              className="relative flex items-center h-full"
              onMouseEnter={() => setIsResourcesHovered(true)}
              onMouseLeave={() => setIsResourcesHovered(false)}
            >
              <button type="button" className="nav-link" onClick={() => setIsResourcesHovered(!isResourcesHovered)}>
                Resources
              </button>
                  <AnimatePresence>
                    {isResourcesHovered && (
                      <motion.div
                        initial={{ opacity: 0, y: 5, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 5, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-4 bg-[#f8f9fa] backdrop-blur-md rounded-none p-6 min-w-[280px] shadow-[0_10px_40px_rgba(0,0,0,0.15)] border border-gray-200 z-50"
                      >
                        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                          <Link to="/contact" className="text-[12px] text-gray-600 font-semibold uppercase tracking-widest hover:text-black transition-colors">
                            Contact
                          </Link>
                          <Link to="/blog" className="text-[12px] text-gray-600 font-semibold uppercase tracking-widest hover:text-black transition-colors">
                            Blog
                          </Link>
                          <Link to="/security" className="text-[12px] text-gray-600 font-semibold uppercase tracking-widest hover:text-black transition-colors">
                            Security
                          </Link>
                          <Link to="/about" className="text-[12px] text-gray-600 font-semibold uppercase tracking-widest hover:text-black transition-colors">
                            About
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
            </div>
          </nav>

          <button type="button" className="sign-in-btn desktop-only" onClick={() => window.location.href = '/user/signup/'}>
            Get Started
          </button>

          <button
            type="button"
            className={`burger-btn ${isMenuOpen ? "open" : ""}`}
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
          >
            <div className="burger-bar" />
            <div className="burger-bar" />
            <div className="burger-bar" />
          </button>
        </header>
      </motion.div>
      )}

      {/* State 2: Background Plate (Default everywhere else, expands on scroll for hero section) */}
      <motion.div
        initial={false}
        animate={{
          left: showBlackNavbar ? "0%" : "30%",
          right: showBlackNavbar ? "0%" : "30%",
          borderRadius: showBlackNavbar ? "0px" : "999px",
          opacity: showBlackNavbar ? 1 : 0,
          y: showBlackNavbar ? 0 : -20,
        }}
        transition={hasHero ? { duration: 0.5, ease: [0.22, 1, 0.36, 1] } : { duration: 0 }}
        className="fixed top-0 z-40 bg-black/90 backdrop-blur-md h-24 pointer-events-none border-b border-gray-800"
      />

      {/* State 2: Content (Default everywhere else, fades in on scroll for hero section) */}
      <AnimatePresence>
        {showBlackNavbar && (
          <motion.div
            initial={hasHero ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            exit={hasHero ? { opacity: 0 } : undefined}
            transition={hasHero ? { duration: 0.3, delay: isScrolled ? 0.1 : 0 } : { duration: 0 }}
            className="fixed top-0 left-0 right-0 z-50 h-24 pointer-events-none flex items-center"
          >
            <div className="w-full max-w-[1400px] mx-auto px-10 lg:px-16 pointer-events-auto">
              <div className="flex items-center justify-between">
                {/* Left section - Logo */}
                <div
                  className="flex items-center gap-4 flex-shrink-0 cursor-pointer"
                  onClick={handleLogoClick}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleLogoClick(); }}
                  tabIndex={0}
                  role="button"
                >
                  <div className="w-10 h-10 flex items-center justify-center">
                    <Logo className="w-full h-full text-white" />
                  </div>
                  <span className="text-white font-medium text-3xl tracking-wide">Cynaps</span>
                </div>

                {/* Center section - Nav Links */}
                <div className="hidden md:flex items-center gap-16 flex-1 justify-center">
                  <a
                    href="/docs"
                    className="text-gray-300 hover:text-white text-[15px] font-medium tracking-[0.2em] cursor-pointer transition-colors"
                  >
                    DOCS
                  </a>

                  <div
                    className="relative flex items-center h-full"
                    onMouseEnter={() => setIsResourcesHovered(true)}
                    onMouseLeave={() => setIsResourcesHovered(false)}
                  >
                    <button type="button" className="text-gray-300 hover:text-white text-[15px] font-medium tracking-[0.2em] cursor-pointer transition-colors py-4">
                      RESOURCES
                    </button>
                    <AnimatePresence>
                      {isResourcesHovered && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-1/2 -translate-x-1/2 bg-[#f4f4f5] border border-gray-300 p-8 min-w-[320px] rounded-none shadow-2xl mt-6"
                        >
                          <div className="grid grid-cols-2 gap-x-10 gap-y-6">
                            <Link to="/contact" className="text-gray-600 text-[13px] tracking-widest hover:text-black cursor-pointer transition-colors font-semibold uppercase">
                              Contact
                            </Link>
                            <Link to="/blog" className="text-gray-600 text-[13px] tracking-widest hover:text-black cursor-pointer transition-colors font-semibold uppercase">
                              Blog
                            </Link>
                            <Link to="/security" className="text-gray-600 text-[13px] tracking-widest hover:text-black cursor-pointer transition-colors font-semibold uppercase">
                              Security
                            </Link>
                            <Link to="/about" className="text-gray-600 text-[13px] tracking-widest hover:text-black cursor-pointer transition-colors font-semibold uppercase">
                              About
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Right section - Auth buttons */}
                <div className="flex items-center gap-10 flex-shrink-0">
                  <button type="button" className="bg-white text-black px-8 py-3 text-[13px] font-bold tracking-widest hover:bg-gray-200 transition-colors hidden md:block" onClick={() => window.location.href = '/user/signup/'}>
                    GET STARTED
                  </button>
                  
                  {/* Mobile burger for scrolled state */}
                  <button
                    type="button"
                    className="md:hidden text-white flex flex-col justify-center gap-[6px] w-8 h-8"
                    onClick={toggleMenu}
                  >
                    <div className="w-full h-[2px] bg-white rounded-full" />
                    <div className="w-full h-[2px] bg-white rounded-full" />
                    <div className="w-full h-[2px] bg-white rounded-full" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <div className={`mobile-overlay ${isMenuOpen ? "visible" : ""}`} onClick={closeMenu} />
      <div className={`mobile-sheet ${isMenuOpen ? "visible" : ""}`} aria-hidden={!isMenuOpen}>
        <a href="/docs" className="mobile-link" onClick={closeMenu}>
          Docs
        </a>
        <a href="#resources" className="mobile-link" onClick={closeMenu}>
          Resources
        </a>
        <Link to="/contact" className="mobile-link" onClick={closeMenu}>
          Contact
        </Link>
        <Link to="/blog" className="mobile-link" onClick={closeMenu}>
          Blog
        </Link>
        <Link to="/security" className="mobile-link" onClick={closeMenu}>
          Security
        </Link>
        <Link to="/about" className="mobile-link" onClick={closeMenu}>
          About
        </Link>
        <button type="button" className="sign-in-btn" onClick={() => { closeMenu(); window.location.href = '/user/signup/'; }}>
          Get Started
        </button>
      </div>
    </>
  );
};

export default Navigation;
